import {
  PurchaseTimingFirst24Hours,
  PurchaseTimingFirst30Days,
  PurchaseTimingOrders1And2,
  PurchaseTimingOrders2And3,
} from 'components/analytics';
import { BackToTop, DateSelector } from 'components/common';
import { ComposableFilters } from 'components/common/ComposableFilters';
import { SegmentSelector } from 'components/common/SegmentSelector';
import { ExternalLinkIcon } from 'components/icons';
import { useEffect } from 'react';
import { toast } from 'react-toastify';
import { useAppSelector } from 'store';
import { composableFiltersState } from 'store/composableFiltersSlice';
import { useGetCustomerPurchaseTimingQuery } from 'store/services/segmentsApi';
import { usePageTitle, useParseURLQueryParams } from 'utils';

export default function PurchaseTiming() {
  usePageTitle('Purchase timing | Segments');

  const { serializedAllQueryParams, rawQueryParams } = useParseURLQueryParams();

  const {
    output: { filtersApiPayload },
  } = useAppSelector(composableFiltersState);

  const { data: purchaseTiming, isFetching } =
    useGetCustomerPurchaseTimingQuery(
      { serializedParams: serializedAllQueryParams, body: filtersApiPayload },
      {
        skip:
          !!!rawQueryParams?.store_name ||
          !!!rawQueryParams?.start_date ||
          !!!rawQueryParams?.end_date,
      }
    );

  const warningMessage = purchaseTiming?.warning;

  useEffect(() => {
    if (warningMessage) {
      toast.info(warningMessage);
    }
  }, [warningMessage]);

  return (
    <div className="relative max-w-screen-xl px-5 mx-auto md:pl-segments-72px md:pr-20 py-11">
      <p className="font-bold leading-6">Analytics</p>
      <h1 className="mt-4 font-bold leading-10 text-segments-44px">
        Purchase timing
      </h1>
      <p className="mt-3 text-black">
        Analyze customer purchase timing to maximize marketing efforts.
      </p>
      <a
        href="https://help.tresl.co/en/articles/5395983-purchase-timing"
        className="inline-flex items-start space-x-0.5 text-segments-link font-bold mt-2"
        target="_blank"
        rel="noreferrer"
      >
        <p>Learn more</p>
        <ExternalLinkIcon className="inline w-5 h-5 align-middle" />
      </a>
      <div className="relative flex items-center w-full space-x-4 mt-7">
        <div className="w-full pl-5 pr-6 text-base align-middle border h-segments-60px bg-segments-accent-green-3 rounded-2xl border-">
          <div className="flex items-center justify-between h-full align-middle">
            <SegmentSelector />
            <ComposableFilters />
          </div>
        </div>
        <div className="2xl:flex-shrink-0">
          <DateSelector defaultPeriod="Last 365 days" />
        </div>
      </div>
      <p className="mt-1.5 text-segments-13px  text-black text-opacity-50">
        Important: the following analysis only considers valid orders.{' '}
        <a
          href="https://help.tresl.co/en/articles/5432306-valid-orders"
          className="inline-flex items-start space-x-0.5 text-segments-link font-bold mt-2"
          target="_blank"
          rel="noreferrer"
        >
          Read more
          <ExternalLinkIcon className="inline ml-0.5 w-4 h-4 align-middle" />
        </a>
      </p>
      <div className="mt-14">
        <p className="text-2xl leading-7">
          Between signing up and first purchase
        </p>
        <p className="mt-2 text-base">
          The two graphs below represent the distribution of when New signups
          convert to One timers.
        </p>
      </div>
      {/* Chart Area */}
      <PurchaseTimingFirst24Hours data={purchaseTiming} loading={isFetching} />
      <PurchaseTimingFirst30Days data={purchaseTiming} loading={isFetching} />
      <PurchaseTimingOrders1And2 data={purchaseTiming} loading={isFetching} />
      <PurchaseTimingOrders2And3 data={purchaseTiming} loading={isFetching} />
      <BackToTop />
    </div>
  );
}
